// Maps a review's colourway name to an avatar swatch colour (per the design's
// coloured-initial avatars). Falls back to a deterministic palette pick for any
// colourway not explicitly listed, so new colourways always get a stable colour.

interface Swatch {
    bg: string
    text: string
}

// Known Kibu colourways + a few design-doc aliases. Hexes approximate the product
// colours; text colour is chosen for contrast against the swatch.
const COLORWAY_SWATCHES: Record<string, string> = {
    'butter popcorn': '#FFD66B',
    'midnight berry': '#3A2E63',
    'forest green': '#4E754F',
    'arctic blue': '#9FC6E0',
    'desert peach': '#FFB59E',
    'sunset orange': '#FF7A45',
    'mudpie': '#885836',
    'seashell': '#F1ECE2',
    'custom': '#B6CFA0',
    // design-doc aliases
    'tangerine': '#FF7A45',
    'pistachio': '#B6CFA0',
    'pearl': '#F1ECE2',
    'blueberry': '#4954D2',
    'buttercup': '#FFD66B',
    'peach': '#FFB59E',
}

// Fallback palette for unknown colourways (and for missing data).
const FALLBACK_PALETTE = ['#FFD66B', '#B6CFA0', '#FFB59E', '#4954D2', '#885836', '#FF7A45']

/** Relative luminance check → black or white text for legibility on the swatch. */
const contrastText = (hex: string): string => {
    const clean = hex.replace('#', '')
    if (clean.length !== 6) return '#000'
    const r = parseInt(clean.slice(0, 2), 16)
    const g = parseInt(clean.slice(2, 4), 16)
    const b = parseInt(clean.slice(4, 6), 16)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.6 ? '#000' : '#fff'
}

const hashIndex = (value: string, length: number): number => {
    let hash = 0
    for (let i = 0; i < value.length; i += 1) {
        hash = (hash * 31 + value.charCodeAt(i)) >>> 0
    }
    return hash % length
}

export const getColorwaySwatch = (colorway?: string | null, seed = ''): Swatch => {
    const key = (colorway || '').trim().toLowerCase()
    const bg = COLORWAY_SWATCHES[key]
        || FALLBACK_PALETTE[hashIndex(key || seed || 'kibu', FALLBACK_PALETTE.length)]
    return { bg, text: contrastText(bg) }
}

/** First initial for the avatar, from the review author's display name. */
export const getInitial = (name?: string | null): string => {
    const trimmed = (name || '').trim()
    return trimmed ? trimmed[0].toUpperCase() : 'K'
}
